import {
  Button,
  Dialog,
  DialogContent,
  Box,
  Stack,
  Typography,
  Tooltip,
  TextField,
  Avatar,
  AvatarGroup,
  IconButton,
} from '@mui/material';
import SvgIconStyle from '@components/SvgIconStyle';
import { FC, useEffect, useMemo, useState } from 'react';
import useLocales from '@hooks/useLocales';
import { COLORS } from '../constants';
import { styles } from './styles';
import DialogTitle from '../dialog-title';
import Color from './color';
import { FormProvider, RHFSwitch, RHFTextField } from '~/components/hook-form';
import { Controller, UseFormReturn } from 'react-hook-form';
import LeavePageDialog from './leave-page-dialog';
import { ListFormValues } from '../types';
import { useQuery } from 'react-query';
import { CurrentUser } from '~/api/scheme';
import { UserDataJSONAPI } from '~/sections/user-profile/types';

type Props = {
  open: boolean;
  onClose: () => void;
  onClickBack?: () => void;
  isOpenLeavePage: boolean;
  onCancel: () => void;
  onClickLeaveAnyway: () => void;
  onSubmit: () => void;
  onReset: () => void;
  methods: UseFormReturn<ListFormValues>;
  dialogTitle: string;
  submitButtonTitle: string;
  creatableListType: string;
  selectedIds?: string[];
};

const CreateOrEditListDialog: FC<Props> = (props) => {
  const {
    open,
    onClose,
    onClickBack,
    isOpenLeavePage,
    onCancel,
    onClickLeaveAnyway,
    onSubmit,
    methods,
    onReset,
    dialogTitle,
    submitButtonTitle,
    creatableListType,
    selectedIds,
  } = props;
  const { t } = useLocales();

  const visibility = methods.watch('visibility');
  const sharedScope = methods.watch('shared_scope');
  const sharedUserIds = methods.watch('shared_user_ids') || [];

  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (visibility !== 'shared') {
      methods.setValue('shared_user_ids', []);
      methods.setValue('shared_scope', 'all');
    } else if (sharedScope === 'all') {
      methods.setValue('shared_user_ids', []);
    }
  }, [methods, visibility, sharedScope]);

  // Load collaborators when the privacy panel is open, or when the list
  // is already in "shared with specific people" mode so the pill can
  // immediately render avatars on initial open of the edit dialog.
  const shouldLoadCollaborators =
    visibility === 'shared' ? sharedScope === 'specific' || privacyOpen : privacyOpen;
  const { data: collaboratorsData } = useQuery(
    ['company-users', 'asc'],
    () => CurrentUser.Company.Users.index({ queryKey: ['', 'Alphabetical [A-Z]'] }),
    { enabled: shouldLoadCollaborators }
  );

  const collaborators: UserDataJSONAPI[] = collaboratorsData?.data?.data || [];
  const collaboratorsById = useMemo(
    () =>
      collaborators.reduce((acc, user) => {
        acc[user.id] = user;
        return acc;
      }, {} as Record<string, UserDataJSONAPI>),
    [collaborators]
  );

  const filteredCollaborators = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return collaborators;

    return collaborators.filter((user) => {
      const { first_name, last_name, email } = user.attributes;
      const fullName = `${first_name} ${last_name}`.toLowerCase();
      return fullName.includes(term) || (email && email.toLowerCase().includes(term));
    });
  }, [collaborators, searchTerm]);

  const sharedCount =
    sharedScope === 'all' ? collaborators.length : (sharedUserIds as string[]).length;

  const privacyLabel = useMemo(() => {
    if (visibility === 'private') {
      return t('dashboard.search.results.create_edit_list.private_only_me');
    }

    if (visibility === 'shared') {
      if (sharedScope === 'all') {
        return t('dashboard.search.results.create_edit_list.shared_all');
      }

      if (sharedCount > 0) {
        return t('dashboard.search.results.create_edit_list.shared_specific');
      }

      return t('dashboard.search.results.create_edit_list.shared_specific');
    }

    return '';
  }, [visibility, sharedScope, sharedCount, t]);

  const selectedSharedUsers = useMemo(() => {
    if (visibility !== 'shared' || sharedScope !== 'specific') return [];
    const ids = (sharedUserIds as string[]) || [];
    return ids.map((id) => collaboratorsById[id]).filter((u): u is UserDataJSONAPI => Boolean(u));
  }, [visibility, sharedScope, sharedUserIds, collaboratorsById]);

  return (
    <>
      <Dialog onClose={onClose} open={open}>
        <DialogTitle
          onClose={onClose}
          title={t(`dashboard.search.results.create_edit_list.${dialogTitle}`)}
        />
        <DialogContent dividers sx={styles.dialogContent}>
          <FormProvider
            methods={methods}
            onSubmit={methods.handleSubmit(() => {
              onSubmit();
              // onClickLeaveAnyway cleans up selectedIds,
              // but in case of Opportunities we need them in the following review credits pop-ups
              if (
                creatableListType != 'OpportunityList' ||
                selectedIds === undefined ||
                selectedIds.length === 0
              )
                onClickLeaveAnyway();
            })}
          >
            <Stack direction="row" justifyContent="space-between">
              {COLORS.map((color) => (
                <Color
                  key={color}
                  color={color}
                  activeColor={methods.watch('color')}
                  setActiveColor={() => methods.setValue('color', color)}
                />
              ))}
            </Stack>
            <RHFTextField
              name="name"
              size="small"
              label={t('dashboard.search.results.create_edit_list.name')}
              sx={styles.textField}
              required={true}
            />
            <RHFTextField
              name="description"
              label={t('dashboard.search.results.create_edit_list.description')}
              multiline
              rows={5}
              sx={styles.textField}
            />
            <Box mt="15px">
              <RHFSwitch
                name="synced"
                label={
                  <Box sx={styles.switchTextContainer}>
                    <Typography variant="body2">
                      {t('dashboard.search.results.create_edit_list.synced_with_bpd')}
                    </Typography>
                    <SvgIconStyle
                      src="/icons/bpd.svg"
                      sx={methods.watch('synced') ? styles.switchActiveIcon : styles.switchIcon}
                    />
                  </Box>
                }
              />
            </Box>
            <Box sx={styles.privacySection}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('dashboard.lists.privacy')}
              </Typography>
              {/* Collapsed pill selector */}
              <Box
                sx={styles.privacySelector}
                onClick={() => setPrivacyOpen((openState) => !openState)}
              >
                <Box sx={styles.privacySelectorLabel}>
                  <SvgIconStyle
                    src={
                      visibility === 'private'
                        ? '/icons/lock/24/outline.svg'
                        : '/icons/share/24/outline.svg'
                    }
                    sx={{ width: 20, height: 20 }}
                  />
                  {visibility === 'shared' &&
                  sharedScope === 'specific' &&
                  selectedSharedUsers.length > 0 ? (
                    <AvatarGroup
                      // show up to 3 avatars, then "+N" if more remain
                      max={4}
                      sx={{
                        '& .MuiAvatar-root': {
                          width: 24,
                          height: 24,
                          fontSize: 10,
                        },
                      }}
                    >
                      {selectedSharedUsers.map((user) => (
                        <Avatar
                          key={user.id}
                          src={user.attributes.avatar_url}
                          alt={`${user.attributes.first_name} ${user.attributes.last_name}`}
                        >
                          {`${user.attributes.first_name?.[0] || ''}${
                            user.attributes.last_name?.[0] || ''
                          }`}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                  ) : (
                    <Typography variant="body2">{privacyLabel}</Typography>
                  )}
                </Box>
                <SvgIconStyle
                  src={
                    privacyOpen
                      ? '/icons/iconly/light/arrow_down_2.svg'
                      : '/icons/iconly/light/arrow_left_2.svg'
                  }
                  sx={{ width: 16, height: 16 }}
                />
              </Box>

              {/* Expanded panel */}
              {privacyOpen && (
                <Stack sx={styles.privacyCardsContainer} mt={2}>
                  {/* Private header + Only me row */}
                  <Controller
                    name="visibility"
                    control={methods.control}
                    render={({ field }) => {
                      const selected = field.value === 'private';
                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SvgIconStyle
                              src="/icons/lock/24/outline.svg"
                              sx={{ width: 20, height: 20 }}
                            />
                            <Typography variant="subtitle2">
                              {t('dashboard.lists.private')}
                            </Typography>
                          </Box>
                          <Box sx={styles.privacyCard} onClick={() => field.onChange('private')}>
                            <Box sx={styles.optionCheckSlot}>
                              {selected && (
                                <SvgIconStyle
                                  src="/icons/approve_tick/24/outline.svg"
                                  sx={{ width: 20, height: 20, color: 'primary.main' }}
                                />
                              )}
                            </Box>
                            <Typography variant="body2">
                              {t('dashboard.search.results.create_edit_list.private_only_me')}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }}
                  />

                  {/* Divider between Private and Shared */}
                  <Box
                    sx={{
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      my: 1.5,
                    }}
                  />

                  {/* Shared section with nested selection */}
                  <Controller
                    name="visibility"
                    control={methods.control}
                    render={({ field }) => {
                      const selected = field.value === 'shared';

                      return (
                        <Box
                          sx={{
                            ...styles.privacyCard,
                            flexDirection: 'column',
                          }}
                        >
                          <Box sx={{ display: 'flex', width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SvgIconStyle
                                src="/icons/share/24/outline.svg"
                                sx={{ width: 20, height: 20 }}
                              />
                              <Typography variant="subtitle2">
                                {t('dashboard.lists.shared')}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={styles.sharedPanel}>
                            <TextField
                              variant="outlined"
                              size="small"
                              placeholder=""
                              sx={styles.sharedSearchField}
                              fullWidth
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <SvgIconStyle
                                    src="/icons/iconly/light/search.svg"
                                    sx={{ width: 20, height: 20, ml: 0.5, mr: 1 }}
                                  />
                                ),
                              }}
                            />

                            <Box sx={styles.sharedList}>
                              {/* Everyone option */}
                              <Controller
                                name="shared_scope"
                                control={methods.control}
                                render={({ field }) => (
                                  <Box
                                    sx={styles.sharedListItem}
                                    onClick={() => {
                                      methods.setValue('visibility', 'shared');
                                      field.onChange('all');
                                      methods.setValue('shared_user_ids', []);
                                    }}
                                  >
                                    <Box sx={styles.optionCheckSlot}>
                                      {visibility === 'shared' && field.value === 'all' && (
                                        <SvgIconStyle
                                          src="/icons/approve_tick/24/outline.svg"
                                          sx={{ width: 20, height: 20, color: 'primary.main' }}
                                        />
                                      )}
                                    </Box>
                                    <Box sx={styles.sharedListItemLeft}>
                                      <Typography variant="body2">
                                        {t(
                                          'dashboard.search.results.create_edit_list.shared_all_label'
                                        )}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              />

                              {/* Specific users list */}
                              <Controller
                                name="shared_user_ids"
                                control={methods.control}
                                render={({ field }) => (
                                  <>
                                    {filteredCollaborators.map((user) => {
                                      const selectedIds = field.value || [];
                                      const isSelected = selectedIds.includes(user.id);
                                      return (
                                        <Box
                                          key={user.id}
                                          sx={styles.sharedListItem}
                                          onClick={() => {
                                            const next = isSelected
                                              ? selectedIds.filter((id: string) => id !== user.id)
                                              : [...selectedIds, user.id];

                                            if (next.length === 0) {
                                              // No users selected: revert back to Private / Only me
                                              methods.setValue('visibility', 'private');
                                              methods.setValue('shared_scope', 'all');
                                            } else {
                                              // At least one user selected: Shared / Specific people
                                              methods.setValue('visibility', 'shared');
                                              methods.setValue('shared_scope', 'specific');
                                            }

                                            field.onChange(next);
                                          }}
                                        >
                                          <Box sx={styles.optionCheckSlot}>
                                            {isSelected && (
                                              <SvgIconStyle
                                                src="/icons/approve_tick/24/outline.svg"
                                                sx={{
                                                  width: 20,
                                                  height: 20,
                                                  color: 'primary.main',
                                                }}
                                              />
                                            )}
                                          </Box>
                                          <Box sx={styles.sharedListItemLeft}>
                                            <Avatar
                                              src={user.attributes.avatar_url}
                                              alt={`${user.attributes.first_name} ${user.attributes.last_name}`}
                                              sx={{ width: 24, height: 24 }}
                                            />
                                            <Typography variant="body2">
                                              {user.attributes.first_name}{' '}
                                              {user.attributes.last_name}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                  </>
                                )}
                              />
                            </Box>
                          </Box>
                        </Box>
                      );
                    }}
                  />
                </Stack>
              )}
            </Box>
            <Stack direction="row" sx={styles.footer}>
              <Box>
                {!!onClickBack && (
                  <Button
                    variant="outlined"
                    size="medium"
                    color="inherit"
                    startIcon={
                      <SvgIconStyle
                        src="/icons/iconly/light/arrow_left_square.svg"
                        sx={styles.icon}
                      />
                    }
                    onClick={onClickBack}
                  >
                    {t('dashboard.search.results.create_edit_list.back')}
                  </Button>
                )}
              </Box>
              <Stack direction="row" spacing="16px">
                <Button variant="text" size="medium" color="inherit" onClick={onClose}>
                  {t('dashboard.search.results.create_edit_list.cancel')}
                </Button>
                <Button type="submit" variant="contained" size="medium" color="primary">
                  {t(`dashboard.search.results.create_edit_list.${submitButtonTitle}`)}
                </Button>
              </Stack>
            </Stack>
          </FormProvider>
        </DialogContent>
      </Dialog>
      <LeavePageDialog
        title={t('dashboard.search.results.leave_page.title')}
        description={t('dashboard.search.results.leave_page.description')}
        button_text={t('dashboard.search.results.leave_page.leave_anyway')}
        open={isOpenLeavePage}
        onCancel={onCancel}
        onClickLeaveAnyway={() => {
          onClickLeaveAnyway();
          onReset();
        }}
      />
    </>
  );
};

export default CreateOrEditListDialog;
